import React, { useState, useEffect, useMemo } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../hooks/useToast';
import Button from '../../components/shared/Button';
import Modal from '../../components/shared/Modal';
import Combobox from '../../components/shared/Combobox';
import { VEHICLE_STATUS, STATUS_LABELS, STATUS_COLORS } from '../../constants/statuses';
import { getAllTransporters } from '../../services/firestore/transporterService';
import { getVehicleTypes } from '../../services/business/vehicleTypeService';
import { getVehicleMovementLogByReservation } from '../../services/logging/vehicleMovementLogger';
import { VEHICLE_MOVEMENT_LABELS } from '../../constants/systemConfig';
import {
    searchVehicles,
    processVehicleEntry,
    processVehicleExit,
    sendToParking,
    createInstantReservation,
    getTodayStatistics,
    findReservationByQRCode,
    updateVehicleDetails
} from '../../services/business/gateService';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const MODE = {
    ARRIVAL: 'arrival',
    DEPARTURE: 'departure'
};

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const isTouchDevice = () => {
    if (typeof window === "undefined") return false;
    return (
        "ontouchstart" in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0
    );
};

const useTilt = (maxTilt = 10) => {
    const ref = React.useRef(null);
    const [style, setStyle] = useState({});
    const [glow, setGlow] = useState({ x: 50, y: 50, o: 0 });

    const onMove = (e) => {
        if (!ref.current) return;
        if (isTouchDevice()) return;

        const rect = ref.current.getBoundingClientRect();
        const px = ((e.clientX - rect.left) / rect.width) * 100;
        const py = ((e.clientY - rect.top) / rect.height) * 100;

        const dx = (e.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
        const dy = (e.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);

        const rotateY = clamp(dx * maxTilt, -maxTilt, maxTilt);
        const rotateX = clamp(-dy * maxTilt, -maxTilt, maxTilt);

        setGlow({ x: px, y: py, o: 1 });

        setStyle({
            transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-8px)`,
        });
    };

    const onLeave = () => {
        setGlow((g) => ({ ...g, o: 0 }));
        setStyle({
            transform: `perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0px)`,
            transition: "transform 400ms ease",
        });

        setTimeout(() => setStyle({}), 400);
    };

    return { ref, style, glow, onMove, onLeave };
};

const PremiumCard = ({ title, description, gradient, ledColor, onClick, active, statsValue, large = false }) => {
    const { ref, style, glow, onMove, onLeave } = useTilt(large ? 10 : 15);

    return (
        <div className="relative" style={{ width: '100%' }}>
            <div className="group relative">
                <div
                    ref={ref}
                    onMouseMove={onMove}
                    onMouseLeave={onLeave}
                    onClick={onClick}
                    className="relative overflow-hidden cursor-pointer will-change-transform"
                    style={{
                        height: large ? '280px' : '135px',
                        padding: '28px',
                        borderRadius: '2.5rem',
                        background: gradient,
                        color: 'white',
                        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                        border: active ? '4px solid rgba(255,255,255,1)' : `2px solid ${ledColor}`,
                        opacity: active ? 1 : 0.95,
                        transform: active ? 'scale(1.02)' : undefined,
                        zIndex: 1,
                        boxShadow: active
                            ? `0 0 30px ${ledColor}, 0 0 60px ${ledColor}, inset 0 0 20px rgba(255,255,255,0.3)`
                            : `0 10px 30px rgba(0,0,0,0.3), 0 0 15px ${ledColor}, inset 0 0 10px ${ledColor}`,
                        ...style
                    }}
                >
                    {/* Parallax Light (Glow inside) */}
                    <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{ opacity: glow.o }}>
                        <div className="absolute inset-0" style={{ background: `radial-gradient(350px circle at ${glow.x}% ${glow.y}%, rgba(255,255,255,0.5), transparent 80%)` }} />
                    </div>

                    {/* Vertical Split Line */}
                    <div className="absolute inset-y-0 left-1/2 w-[1.5px] bg-white/10 z-10" />

                    {/* Soft highlight blobs */}
                    <div className="pointer-events-none absolute inset-0" style={{ opacity: 0.2 }}>
                        <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-white blur-[60px]" />
                        <div className="absolute -bottom-20 -left-20 h-56 w-56 rounded-full bg-black blur-[60px]" />
                    </div>

                    {/* Content */}
                    <div className="relative z-20 h-full flex flex-col justify-between" style={{ pointerEvents: 'none' }}>
                        <div className="flex justify-between items-start">
                            <div style={{ flex: 1 }}>
                                <h3 style={{
                                    fontSize: large ? '3.5rem' : '0.9rem',
                                    fontWeight: '900',
                                    margin: 0,
                                    letterSpacing: large ? '-2px' : '0px',
                                    textTransform: 'uppercase',
                                    color: 'white',
                                    lineHeight: 1
                                }}>
                                    {title}
                                </h3>
                                {description && (
                                    <p style={{
                                        fontSize: '0.75rem',
                                        fontWeight: '800',
                                        opacity: 0.9,
                                        marginTop: '12px',
                                        letterSpacing: '4px',
                                        textTransform: 'uppercase',
                                        color: 'white'
                                    }}>
                                        {description}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-between items-end">
                            {statsValue !== undefined ? (
                                <div style={{
                                    fontSize: '4.5rem',
                                    fontWeight: '900',
                                    letterSpacing: '-3px',
                                    color: 'white',
                                    filter: 'drop-shadow(0 10px 10px rgba(0,0,0,0.3))',
                                    lineHeight: 0.8
                                }}>
                                    {statsValue}
                                </div>
                            ) : (
                                <div className="flex justify-end w-full">
                                    <div style={{
                                        width: '60px',
                                        height: '60px',
                                        borderRadius: '50%',
                                        border: '3px solid rgba(255,255,255,0.5)',
                                        background: active ? 'white' : 'rgba(255,255,255,0.2)',
                                        color: active ? 'black' : 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backdropFilter: 'blur(15px)',
                                        boxShadow: active ? `0 0 20px white` : 'none'
                                    }}>
                                        <span className="material-icons" style={{ fontSize: '2rem', fontWeight: '900' }}>{active ? 'done_all' : 'east'}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const GateEntryExit = ({ userData }) => {
    const { user } = usePermissions(userData);
    const { success: toastSuccess, error: toastError } = useToast();

    // State
    const [mode, setMode] = useState(MODE.ARRIVAL);
    const [loading, setLoading] = useState(false);
    const [reservations, setReservations] = useState([]);
    const [transporters, setTransporters] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [dateRange, setDateRange] = useState({
        start: new Date().toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
    });
    const [selectedDateOption, setSelectedDateOption] = useState('bugün');
    const [currentTime, setCurrentTime] = useState(new Date());

    const DATE_OPTIONS = [
        { label: 'BUGÜN', val: 'bugün', days: 0 },
        { label: 'SON 3 GÜN', val: '3gün', days: 3 },
        { label: 'SON 5 GÜN', val: '5gün', days: 5 },
        { label: 'SON 1 HAFTA', val: '1hafta', days: 7 },
    ];

    // Modals
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [actionModal, setActionModal] = useState({ open: false, type: null });
    const [instantModal, setInstantModal] = useState(false);
    const [qrScannerModal, setQrScannerModal] = useState(false);
    const [qrInput, setQrInput] = useState('');
    const [actionNote, setActionNote] = useState('');
    const [processing, setProcessing] = useState(false);
    const [vehicleHistory, setVehicleHistory] = useState([]);

    // Instant Form
    const [instantForm, setInstantForm] = useState({
        plateNumber: '',
        transporterId: null,
        transporterName: '',
        vehicleTypeId: null,
        vehicleType: '',
        driverName: '',
        driverPhone: '',
        driverIdentityId: '',
        notes: ''
    });
    const [activeFilter, setActiveFilter] = useState(null);

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (user?.tenantId) {
            loadInitialData();
        }
    }, [user?.tenantId]);

    // Update dateRange when selectedDateOption changes
    useEffect(() => {
        const end = new Date();
        const start = new Date();
        const option = DATE_OPTIONS.find(o => o.val === selectedDateOption);
        if (option) {
            start.setDate(end.getDate() - option.days);
        }
        setDateRange({
            start: start.toISOString().split('T')[0],
            end: end.toISOString().split('T')[0]
        });
    }, [selectedDateOption]);

    useEffect(() => {
        if (user?.tenantId) {
            loadReservations();
        }
    }, [user?.tenantId, dateRange, searchQuery]);

    const loadInitialData = async () => {
        try {
            const [tData, vtData] = await Promise.all([
                getAllTransporters(user.tenantId),
                getVehicleTypes(user.tenantId)
            ]);
            setTransporters(tData);
            setVehicleTypes(vtData);
        } catch (err) { }
    };

    // Removed loadStatistics as it's replaced by useMemo

    const loadReservations = async () => {
        setLoading(true);
        try {
            const data = await searchVehicles(user.tenantId, {
                plate: searchQuery,
                startDate: dateRange.start,
                endDate: dateRange.end
            });
            setReservations(data);
        } catch (err) {
            toastError("Veriler yüklenirken hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    // Calculate statistics from the current reservations list
    const statistics = useMemo(() => {
        const stats = { total: reservations.length, entered: 0, inParking: 0, loading: 0, exited: 0, arrival: 0, departure: 0 };
        reservations.forEach(r => {
            const s = (r.status || '').toLowerCase();
            const entered = s.includes('entered') || s.includes('checked_in') || s.includes('arrived') || r.entryTime;
            const exited = s.includes('exited') || s.includes('checked_out') || r.exitTime;
            const parking = s.includes('parking');
            const loading = s === 'loading_started' || s === 'loading' || s === 'in_progress';
            const loadComp = s.includes('completed') || s.includes('finished') || s.includes('loading_completed');

            // Custom Rule: "Tesisteki Araç"
            if (entered && !exited && !parking && !loading && !loadComp) stats.entered++;
            if (parking && !exited) stats.inParking++;
            if (loading && !exited) stats.loading++;
            if (exited) stats.exited++;

            // Arrival Count: Not inside and not exited
            const isInsideOrDone = entered || parking || loading || loadComp || exited;
            if (!isInsideOrDone) stats.arrival++;

            // Departure Count: Loading completed but not exited
            if (loadComp && !exited) stats.departure++;
        });
        return stats;
    }, [reservations]);

    const filteredReservations = useMemo(() => {
        let list = reservations;

        if (activeFilter) {
            list = list.filter(res => {
                const s = (res.status || '').toLowerCase();
                const entered = s.includes('entered') || s.includes('checked_in') || s.includes('arrived') || res.entryTime;
                const exited = s.includes('exited') || s.includes('checked_out') || res.exitTime;
                const parking = s.includes('parking');
                const loading = s === 'loading_started' || s === 'loading';
                const loadComp = s.includes('completed') || s.includes('finished');

                switch (activeFilter) {
                    case 'entered': // Tesisteki Araç
                        return entered && !exited && !parking && !loading && !loadComp;
                    case 'inParking':
                        return parking && !exited;
                    case 'loading': // Rampadakiler
                        return loading && !exited;
                    case 'all': // Hepsi
                        return true;
                    default:
                        return true;
                }
            });
            return list;
        }

        // Mode Filters (Geliş / Gidiş)
        if (mode === MODE.ARRIVAL) {
            // "Giriş yapılmamış araçlar" - Show only if NOT inside and NOT exited
            return list.filter(res => {
                const s = (res.status || '').toLowerCase();
                const isInsideOrDone =
                    res.entryTime ||
                    s.includes('entered') ||
                    s.includes('checked_in') ||
                    s.includes('parking') ||
                    s.includes('loading') ||
                    s.includes('completed') ||
                    s.includes('finished') ||
                    s.includes('exited') ||
                    res.exitTime;

                return !isInsideOrDone;
            });
        } else if (mode === MODE.DEPARTURE) {
            // "Yüklemesi tamamlanmış (rampadan ayrılmış) ama henüz çıkış yapmamış"
            return list.filter(res => {
                const s = (res.status || '').toLowerCase();
                const isLoadingDone = s.includes('completed') || s.includes('finished') || s.includes('loading_completed');
                const hasExited = s.includes('exited') || res.exitTime;

                return isLoadingDone && !hasExited;
            });
        }

        return list;
    }, [reservations, mode, activeFilter]);

    const handleVehicleClick = async (v) => {
        setSelectedVehicle({
            ...v,
            driverName: v.driverName || v.driver?.name || '',
            driverPhone: v.driverPhone || v.driver?.phone || '',
            driverIdentityId: v.driverIdentityId || ''
        });
        setActionNote('');
        try {
            const log = await getVehicleMovementLogByReservation(v.id);
            setVehicleHistory(log?.movements || []);
        } catch {
            setVehicleHistory([]);
        }
        const s = (v.status || '').toLowerCase();
        const isEntryCandidate =
            mode === MODE.ARRIVAL ||
            s === 'exited' ||
            s === 'facility_exited' ||
            (!mode && (['pending', 'confirmed', 'delayed', 'in_parking'].includes(s)));

        setActionModal({ open: true, type: isEntryCandidate ? 'entry' : 'exit' });
    };

    const handleSaveDetails = async () => {
        if (!selectedVehicle) return;
        setProcessing(true);
        try {
            const updatedDetails = {
                plateNumber: selectedVehicle.plateNumber || '',
                driverName: selectedVehicle.driverName || selectedVehicle.driver?.name || '',
                driverPhone: selectedVehicle.driverPhone || selectedVehicle.driver?.phone || '',
                driverIdentityId: selectedVehicle.driverIdentityId || '',
                transporterId: selectedVehicle.transporterId || null,
                transporterName: selectedVehicle.transporterName || 'Bilinmiyor',
                vehicleType: selectedVehicle.vehicleType || 'Standart'
            };
            await updateVehicleDetails(selectedVehicle.id, updatedDetails, user.uid, user.tenantId);
            toastSuccess("Araç bilgileri güncellendi.");
            // Do not close modal automatically, intended for just saving
            loadReservations();
        } catch (err) {
            toastError("Güncelleme hatası: " + err.message);
        } finally {
            setProcessing(false);
        }
    };

    const handleEntry = async () => {
        if (!selectedVehicle) return;
        setProcessing(true);
        try {
            const updatedDetails = {
                plateNumber: selectedVehicle.plateNumber,
                driverName: selectedVehicle.driverName,
                driverPhone: selectedVehicle.driverPhone,
                driverIdentityId: selectedVehicle.driverIdentityId,
                transporterId: selectedVehicle.transporterId
            };
            await processVehicleEntry(selectedVehicle.id, selectedVehicle.plateNumber, user.tenantId, user.uid, actionNote, updatedDetails);
            toastSuccess(`${selectedVehicle.plateNumber} giriş yaptı.`);
            setActionModal({ open: false, type: null });
            loadReservations();
            // loadStatistics(); // Removed
        } catch (err) {
            toastError(err.message);
        } finally {
            setProcessing(false);
        }
    };

    const handleExit = async () => {
        if (!selectedVehicle) return;
        setProcessing(true);
        try {
            const updatedDetails = {
                plateNumber: selectedVehicle.plateNumber,
                driverName: selectedVehicle.driverName,
                driverPhone: selectedVehicle.driverPhone,
                driverIdentityId: selectedVehicle.driverIdentityId,
                transporterId: selectedVehicle.transporterId
            };
            await processVehicleExit(selectedVehicle.id, user.uid, user.tenantId, actionNote, updatedDetails);
            toastSuccess(`${selectedVehicle.plateNumber} çıkış yaptı.`);
            setActionModal({ open: false, type: null });
            loadReservations();
            // loadStatistics(); // Removed
        } catch (err) {
            toastError(err.message);
        } finally {
            setProcessing(false);
        }
    };

    const handleQuickAction = (m) => {
        setMode(m);
    };

    const handleQRSearch = async () => {
        if (!qrInput) return;
        setProcessing(true);
        try {
            const res = await findReservationByQRCode(user.tenantId, qrInput);
            if (res) {
                handleVehicleClick(res);
                setQrScannerModal(false);
                setQrInput('');
            } else {
                toastError("Rezervasyon bulunamadı.");
            }
        } catch (err) {
            toastError("Arama hatası.");
        } finally {
            setProcessing(false);
        }
    };

    const handleSendToParking = async () => {
        if (!selectedVehicle) return;
        setProcessing(true);
        try {
            const updatedDetails = {
                plateNumber: selectedVehicle.plateNumber,
                driverName: selectedVehicle.driverName,
                driverPhone: selectedVehicle.driverPhone,
                driverIdentityId: selectedVehicle.driverIdentityId,
                transporterId: selectedVehicle.transporterId
            };
            await sendToParking(selectedVehicle.id, selectedVehicle.plateNumber, user.tenantId, user.uid, actionNote, updatedDetails);
            toastSuccess(`${selectedVehicle.plateNumber} parka çekildi.`);
            setActionModal({ open: false, type: null });
            loadReservations();
            // loadStatistics(); // Removed
        } catch (err) {
            toastError("İşlem başarısız: " + err.message);
        } finally {
            setProcessing(false);
        }
    };

    const handleInstantReservation = async () => {
        if (!instantForm.plateNumber) {
            toastError("Plaka gereklidir.");
            return;
        }
        setProcessing(true);
        try {
            await createInstantReservation({
                ...instantForm,
                tenantId: user.tenantId
            }, user.uid);
            toastSuccess(`${instantForm.plateNumber} giriş kaydı oluşturuldu.`);
            setInstantModal(false);
            setInstantForm({ plateNumber: '', transporterId: null, transporterName: '', vehicleTypeId: null, vehicleType: '', driverName: '', driverPhone: '', notes: '' });
            loadReservations();
            // loadStatistics(); // Removed
        } catch (err) {
            toastError("İşlem başarısız: " + err.message);
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="container py-lg" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xl)' }}>


            {/* ULTRA PREMIUM GATEDASHBOARD HEADER */}
            <motion.div
                initial="hidden"
                animate="show"
                variants={{
                    hidden: {},
                    show: { transition: { staggerChildren: 0.1 } }
                }}
                className="grid grid-cols-1 lg:grid-cols-12 gap-lg"
            >
                {/* PORTAL SELECTORS */}
                <div className="lg:col-span-8 grid grid-cols-2 gap-lg">
                    <PremiumCard
                        title="GELİŞ"
                        description="Tesis Giriş Portalı"
                        gradient="linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #d946ef 100%)"
                        ledColor="rgba(168, 85, 247, 0.6)"
                        active={mode === MODE.ARRIVAL}
                        statsValue={statistics.arrival}
                        onClick={() => {
                            setMode(MODE.ARRIVAL);
                            setActiveFilter(null);
                        }}
                        large
                    />
                    <PremiumCard
                        title="GİDİŞ"
                        description="Tesis Çıkış Portalı"
                        gradient="linear-gradient(135deg, #f43f5e 0%, #ef4444 50%, #fb923c 100%)"
                        ledColor="rgba(239, 68, 68, 0.6)"
                        active={mode === MODE.DEPARTURE}
                        statsValue={statistics.departure}
                        onClick={() => {
                            setMode(MODE.DEPARTURE);
                            setActiveFilter(null);
                        }}
                        large
                    />
                </div>

                {/* STATISTICS PANEL */}
                <div className="lg:col-span-4 grid grid-cols-2 gap-md">
                    <PremiumCard
                        title="TESİS İÇİ"
                        gradient="linear-gradient(135deg, #059669 0%, #10b981 100%)"
                        ledColor="rgba(16, 185, 129, 0.6)"
                        statsValue={statistics.entered}
                        active={activeFilter === 'entered'}
                        onClick={() => {
                            setMode(null);
                            setActiveFilter(activeFilter === 'entered' ? null : 'entered');
                        }}
                    />
                    <PremiumCard
                        title="PARK ALANI"
                        gradient="linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)"
                        ledColor="rgba(99, 102, 241, 0.6)"
                        statsValue={statistics.inParking}
                        active={activeFilter === 'inParking'}
                        onClick={() => {
                            setMode(null);
                            setActiveFilter(activeFilter === 'inParking' ? null : 'inParking');
                        }}
                    />
                    <PremiumCard
                        title="RAMPA"
                        gradient="linear-gradient(135deg, #d97706 0%, #f59e0b 100%)"
                        ledColor="rgba(245, 158, 11, 0.6)"
                        statsValue={statistics.loading}
                        active={activeFilter === 'loading'}
                        onClick={() => {
                            setMode(null);
                            setActiveFilter(activeFilter === 'loading' ? null : 'loading');
                        }}
                    />
                    <PremiumCard
                        title="HEPSİ"
                        gradient="linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)"
                        ledColor="rgba(99, 102, 241, 0.6)"
                        statsValue={statistics.total}
                        active={activeFilter === 'all'}
                        onClick={() => {
                            setMode(null);
                            setActiveFilter(activeFilter === 'all' ? null : 'all');
                        }}
                    />
                </div>
            </motion.div>


            {/* SEARCH & ACTIONS TOOLBAR */}
            <div className="flex flex-wrap gap-md items-center">
                {/* DATE SELECTOR BUTTONS */}
                <div className="flex gap-md mr-md">
                    {DATE_OPTIONS.map(opt => {
                        let iconName = 'today';
                        if (opt.val.includes('days') || opt.val.includes('gün')) iconName = 'history';
                        if (opt.val.includes('week') || opt.val.includes('hafta')) iconName = 'calendar_month';

                        const isActive = selectedDateOption === opt.val;

                        return (
                            <button
                                key={opt.val}
                                onClick={() => setSelectedDateOption(opt.val)}
                                className={`
                                    group relative inline-flex items-center justify-center gap-3 px-6 py-4
                                    transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)]
                                    hover:-translate-y-[2px]
                                    active:translate-y-[0px]
                                `}
                                style={{
                                    background: isActive ? 'var(--primary-color)' : 'var(--bg-card)',
                                    backdropFilter: 'blur(12px)',
                                    WebkitBackdropFilter: 'blur(12px)',
                                    border: isActive ? '1px solid rgba(255,255,255,0.2)' : '1px solid var(--border-color)',
                                    boxShadow: isActive ? '0 10px 25px -5px var(--primary-light)' : 'var(--shadow-sm)',
                                    color: isActive ? '#ffffff' : 'var(--text-primary)',
                                    borderRadius: '1.25rem',
                                    minWidth: '160px',
                                    outline: 'none',
                                    overflow: 'hidden'
                                }}
                            >
                                {/* Active Glow Effect */}
                                {isActive && (
                                    <div className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent opacity-50" />
                                )}

                                {/* Icon box */}
                                <span
                                    className="relative z-10 grid place-items-center h-10 w-10 rounded-xl transition-all duration-500 group-hover:scale-110"
                                    style={{
                                        background: isActive ? 'rgba(255,255,255,0.2)' : 'var(--bg-secondary)',
                                        color: isActive ? 'white' : 'var(--text-secondary)',
                                        border: '1px solid var(--border-color)',
                                        boxShadow: isActive ? '0 0 15px rgba(255,255,255,0.2)' : 'none'
                                    }}
                                >
                                    <span className="material-icons text-xl">{iconName}</span>
                                </span>

                                {/* Label */}
                                <span className="relative z-10 text-[11px] font-black tracking-widest uppercase" style={{ textShadow: isActive ? '0 2px 4px rgba(0,0,0,0.3)' : 'none' }}>
                                    {opt.label}
                                </span>

                                {/* Hover Shine */}
                                <div className="absolute top-0 -inset-full h-full w-1/2 z-5 block transform -skew-x-12 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:animate-shine" />
                            </button>
                        );
                    })}
                </div>

                <div className="flex-1" style={{ position: 'relative' }}>
                    <input
                        className="input"
                        style={{
                            width: '100%',
                            paddingLeft: '24px',
                            paddingRight: '60px',
                            paddingTop: '16px',
                            paddingBottom: '16px',
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            fontFamily: 'monospace',
                            borderRadius: '1rem',
                            background: 'var(--bg-card)',
                            border: '1px solid var(--border-color)',
                            color: 'var(--text-primary)',
                            outline: 'none'
                        }}
                        placeholder="PLAKA SEÇ VEYA ARA..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value.toUpperCase())}
                    />
                    <span
                        className="material-icons"
                        style={{
                            position: 'absolute',
                            right: '20px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: 'var(--text-secondary)',
                            opacity: 0.5,
                            pointerEvents: 'none',
                            fontSize: '24px',
                            zIndex: 10
                        }}
                    >
                        search
                    </span>
                </div>

                <Button
                    variant="secondary"
                    className="px-lg py-md h-[60px] rounded-2xl border-white/20"
                    style={{
                        background: 'var(--bg-card)',
                        color: 'var(--text-primary)',
                        boxShadow: 'var(--shadow-md)',
                        border: '1px solid var(--border-color)',
                        transition: 'all 0.3s ease'
                    }}
                    onClick={() => setQrScannerModal(true)}
                >
                    <span className="material-icons">qr_code_scanner</span>
                    <span className="font-bold">Barkod / QR</span>
                </Button>

                <Button
                    variant="primary"
                    className="px-xl py-md h-[60px] rounded-2xl"
                    style={{
                        background: 'var(--primary-color)',
                        color: 'white',
                        boxShadow: '0 8px 32px var(--primary-light)',
                        border: '1px solid var(--primary-hover)',
                        transition: 'all 0.3s ease'
                    }}
                    onClick={() => setInstantModal(true)}
                >
                    <span className="material-icons">add_circle</span>
                    <span className="font-bold">ANLIK GİRİŞ</span>
                </Button>
            </div>

            {/* CONTENT AREA: Vehicle Cards */}
            <div className="flex-1">
                {loading ? (
                    <div className="flex flex-col items-center justify-center p-2xl">
                        <div className="text-4xl animate-bounce">🚛</div>
                        <div className="text-secondary font-bold tracking-widest mt-4">SİSTEM TARANIYOR...</div>
                    </div>
                ) : filteredReservations.length === 0 ? (
                    <div className="card p-2xl text-center flex flex-col items-center border-dashed border-white/20">
                        <span className="text-6xl mb-4 opacity-20">📡</span>
                        <h2 className="text-secondary">Eşleşen Araç Bulunamadı</h2>
                        <p className="text-light">Lütfen plaka aramasını kontrol edin veya yeni kayıt oluşturun.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-lg">
                        {filteredReservations.map(v => (
                            <div
                                key={v.id}
                                className="card group cursor-pointer border-white/5 hover:border-primary/50"
                                onClick={() => handleVehicleClick(v)}
                            >
                                <div className="flex justify-between items-start mb-lg">
                                    <div className="flex flex-col gap-2">
                                        {v.reservationNumber && (
                                            <span className="text-[10px] bg-blue-500/10 text-blue-400 px-1.5 py-0.5 rounded w-fit font-mono tracking-wide border border-blue-500/20">
                                                {v.reservationNumber}
                                            </span>
                                        )}
                                        <div
                                            className="relative group/badge px-6 py-2 rounded-full flex items-center justify-center overflow-hidden transition-transform hover:scale-105"
                                            style={{
                                                backgroundColor: STATUS_COLORS[v.status] || '#334155',
                                                boxShadow: `0 0 20px ${STATUS_COLORS[v.status] || '#334155'}90, inset 0 0 10px rgba(255,255,255,0.3)`,
                                                border: '2px solid rgba(255, 255, 255, 0.4)',
                                                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                                                borderRadius: '50px',
                                                minWidth: '140px',
                                                outline: 'none',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            {/* Inner Shine Effect */}
                                            <div
                                                className="absolute inset-0 pointer-events-none opacity-30"
                                                style={{
                                                    background: 'linear-gradient(180deg, rgba(255,255,255,0.8) 0%, transparent 100%)'
                                                }}
                                            />

                                            <span className="relative z-10 text-xs font-black tracking-widest text-white uppercase">
                                                {STATUS_LABELS[v.status] || v.status.toUpperCase()}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="text-lg font-mono font-bold transition-colors" style={{ color: 'var(--text-light)' }}>
                                        {v.startTime ? new Date(v.startTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                                    </div>
                                </div>

                                <div className="flex justify-center mb-lg group-hover:scale-105 transition-all duration-500">
                                    <span className="font-mono font-black uppercase tracking-tighter whitespace-nowrap transition-all"
                                        style={{
                                            fontSize: '60px',
                                            color: 'var(--text-primary)',
                                            textShadow: '0 0 8px var(--text-primary)',
                                            lineHeight: '1',
                                            display: 'block'
                                        }}>
                                        {v.plateNumber || 'BELİRSİZ'}
                                    </span>
                                </div>

                                <div className="grid grid-cols-2 gap-x-lg gap-y-md pt-lg border-t border-white/5">
                                    <div className="border-r border-white/5 pr-md">
                                        <div className="text-[10px] uppercase font-bold tracking-widest" style={{ color: 'var(--text-light)' }}>Tedarikçi</div>
                                        <div className="text-sm font-bold truncate" style={{ color: 'var(--text-primary)' }}>{v.transporterShortName || v.transporterName || 'Bilinmiyor'}</div>
                                    </div>
                                    <div className="pl-md">
                                        <div className="text-[10px] uppercase font-bold tracking-widest" style={{ color: 'var(--text-light)' }}>Sürücü</div>
                                        <div className="text-sm font-bold truncate" style={{ color: 'var(--text-primary)' }}>{v.driverName || 'Belirtilmedi'}</div>
                                    </div>
                                    <div className="border-r border-white/5 pr-md pt-sm">
                                        <div className="text-[10px] uppercase font-bold tracking-widest" style={{ color: 'var(--text-light)' }}>Rampa</div>
                                        <div className="text-sm font-bold truncate" style={{ color: 'var(--primary-color)', textShadow: '0 0 10px var(--primary-light)' }}>
                                            {v.dockName || v.dock?.name || 'BELİRSİZ'}
                                        </div>
                                    </div>
                                    <div className="pl-md pt-sm">
                                        <div className="text-[10px] uppercase font-bold tracking-widest" style={{ color: 'var(--text-light)' }}>Araç Tipi</div>
                                        <div className="text-sm font-bold truncate" style={{ color: 'var(--text-primary)' }}>{v.vehicleTypeName || v.vehicleType || 'Standart'}</div>
                                    </div>
                                </div>

                                {v.notes && (
                                    <div className="absolute top-4 right-4 text-amber-400 rotate-12 transition-transform group-hover:rotate-0">
                                        <span className="material-icons">sticky_note_2</span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* MODALS */}

            {/* QR Scanner Modal */}
            <Modal isOpen={qrScannerModal} onClose={() => setQrScannerModal(false)} title="QR / Barkod Sorgula">
                <div className="p-lg text-center">
                    <div className="text-6xl mb-md">📸</div>
                    <p className="text-secondary mb-xl">Lütfen araç barkodunu okutun veya kodu girin.</p>
                    <input
                        className="input text-center text-4xl font-mono font-black py-xl tracking-widest border-2"
                        placeholder="KOD GİRİN"
                        value={qrInput}
                        onChange={(e) => setQrInput(e.target.value.toUpperCase())}
                        autoFocus
                    />
                    <div className="flex gap-md mt-2xl">
                        <Button variant="secondary" className="flex-1" onClick={() => setQrScannerModal(false)}>KAPAT</Button>
                        <Button variant="primary" className="flex-1" onClick={handleQRSearch} disabled={!qrInput}>SORGULA</Button>
                    </div>
                </div>
            </Modal>

            {/* Action Modal (Entry/Exit) */}
            <Modal
                isOpen={actionModal.open}
                onClose={() => setActionModal({ open: false, type: null })}
                title={actionModal.type === 'entry' ? "ARAÇ GİRİŞ ONAYI" : "ARAÇ ÇIKIŞ ONAYI"}
            >
                {selectedVehicle && (
                    <div className="p-md">
                        <div className="card text-center p-xl mb-xl bg-black/40 border-2" style={{ borderColor: actionModal.type === 'entry' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)' }}>
                            <div className="relative inline-flex items-center justify-center w-full mb-xl py-3 px-8">
                                <input
                                    className="font-black font-mono tracking-tighter text-center w-full focus:outline-none"
                                    style={{
                                        fontSize: '60px',
                                        background: 'transparent',
                                        border: 'none',
                                        boxShadow: 'none',
                                        padding: '0',
                                        outline: 'none',
                                        textTransform: 'uppercase',
                                        lineHeight: '1',
                                        color: 'var(--text-primary)',
                                        textShadow: '0 0 8px var(--text-primary)'
                                    }}
                                    value={selectedVehicle.plateNumber}
                                    onChange={(e) => setSelectedVehicle({ ...selectedVehicle, plateNumber: e.target.value.toUpperCase() })}
                                />
                                <span className="material-icons absolute right-4 top-1/2 -translate-y-1/2 text-primary opacity-40 pointer-events-none text-2xl">edit</span>
                            </div>
                            <div className="text-secondary font-bold tracking-widest">{selectedVehicle.transporterName}</div>
                        </div>

                        <div className="grid grid-cols-2 gap-lg mb-xl">
                            <div className="bg-white/5 p-md rounded-xl border border-white/5 transition-colors focus-within:bg-white/10 focus-within:border-white/20 relative group">
                                <label className="text-[10px] text-light block mb-1">SÜRÜCÜ ADI</label>
                                <div className="relative">
                                    <input
                                        className="font-bold w-full text-white placeholder-white/30 focus:outline-none"
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            boxShadow: 'none',
                                            padding: '0 24px 0 0',
                                            outline: 'none'
                                        }}
                                        value={selectedVehicle.driverName || selectedVehicle.driver?.name || ''}
                                        onChange={(e) => setSelectedVehicle({ ...selectedVehicle, driverName: e.target.value })}
                                        placeholder="İsim Giriniz"
                                    />
                                    <span className="material-icons absolute right-0 top-0 text-white/20 text-sm group-hover:text-white/50 transition-colors pointer-events-none">edit</span>
                                </div>
                            </div>
                            <div className="bg-white/5 p-md rounded-xl border border-white/5 transition-colors focus-within:bg-white/10 focus-within:border-white/20 relative group">
                                <label className="text-[10px] text-light block mb-1">TELEFON</label>
                                <div className="relative">
                                    <input
                                        className="font-mono font-bold text-primary w-full placeholder-primary/50 focus:outline-none"
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            boxShadow: 'none',
                                            padding: '0 24px 0 0',
                                            outline: 'none'
                                        }}
                                        value={selectedVehicle.driverPhone || selectedVehicle.driver?.phone || ''}
                                        onChange={(e) => setSelectedVehicle({ ...selectedVehicle, driverPhone: e.target.value })}
                                        placeholder="Telefon Giriniz"
                                    />
                                    <span className="material-icons absolute right-0 top-0 text-white/20 text-sm group-hover:text-white/50 transition-colors pointer-events-none">edit</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white/5 p-md rounded-xl border border-white/5 transition-colors focus-within:bg-white/10 focus-within:border-white/20 relative group mb-xl">
                            <label className="text-[10px] text-light block mb-1 uppercase tracking-widest font-bold">TC / KİMLİK NO</label>
                            <div className="relative">
                                <input
                                    className="font-mono font-bold text-white w-full placeholder-white/30 focus:outline-none"
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        boxShadow: 'none',
                                        padding: '0 24px 0 0',
                                        outline: 'none'
                                    }}
                                    value={selectedVehicle.driverIdentityId || ''}
                                    onChange={(e) => setSelectedVehicle({ ...selectedVehicle, driverIdentityId: e.target.value })}
                                    placeholder="TC No / Kimlik No Giriniz"
                                />
                                <span className="material-icons absolute right-0 top-0 text-white/20 text-sm group-hover:text-white/50 transition-colors pointer-events-none">edit</span>
                            </div>
                        </div>

                        <div className="mb-xl">
                            <label className="text-[10px] text-light block mb-2 uppercase font-black tracking-widest">İşlem Notu (Opsiyonel)</label>
                            <textarea
                                className="textarea w-full bg-white/5 border-white/10 rounded-xl"
                                placeholder="Giriş veya çıkışla ilgili önemli bir not bırakın..."
                                value={actionNote}
                                onChange={(e) => setActionNote(e.target.value)}
                            />
                        </div>

                        {vehicleHistory.length > 0 && (
                            <div className="mb-xl p-md bg-black/20 rounded-xl border border-white/5">
                                <label className="text-[10px] text-light block mb-2 uppercase font-black tracking-widest">Geçmiş Hareketler</label>
                                <div className="max-h-[100px] overflow-y-auto pr-2">
                                    {vehicleHistory.map((m, i) => (
                                        <div key={i} className="flex justify-between items-center py-1 border-b border-white/5 last:border-0">
                                            <span className="text-xs text-white/70">{VEHICLE_MOVEMENT_LABELS[m.eventType] || m.eventType}</span>
                                            <span className="text-[10px] font-mono text-secondary">
                                                {m.timestamp?.toDate ? m.timestamp.toDate().toLocaleTimeString() : new Date(m.timestamp).toLocaleTimeString()}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {selectedVehicle.note && (
                            <div className="mb-xl p-md bg-amber-500/10 rounded-xl border border-amber-500/20 relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-1 h-full bg-amber-500/50"></div>
                                <label className="text-[10px] text-amber-500 block mb-2 uppercase font-black tracking-widest flex items-center gap-2">
                                    <span className="material-icons text-sm">sticky_note_2</span> Sipariş Notu / Nakliyeci Notu
                                </label>
                                <p className="text-sm font-semibold text-amber-100 whitespace-pre-wrap italic pl-2">
                                    "{selectedVehicle.note}"
                                </p>
                            </div>
                        )}

                        <div className="flex gap-md">
                            <Button variant="secondary" className="flex-1 !bg-slate-700 hover:!bg-slate-600 !text-white border-none" onClick={() => setActionModal({ open: false, type: null })}>VAZGEÇ</Button>

                            {/* Save Button */}
                            <Button
                                variant="secondary"
                                className="flex-1 !bg-indigo-600 hover:!bg-indigo-500 !text-white border-none"
                                onClick={handleSaveDetails}
                                disabled={processing}
                            >
                                KAYDET
                            </Button>

                            {actionModal.type === 'entry' && (
                                <>
                                    <Button variant="secondary" className="flex-1 !bg-blue-600 hover:!bg-blue-500 !text-white border-none" onClick={handleSendToParking}>PARKA ÇEK</Button>
                                    <Button variant="primary" className="flex-2 !bg-emerald-600 hover:!bg-emerald-500 !text-white border-none shadow-[0_0_30px_rgba(16,185,129,0.4)]" onClick={handleEntry} disabled={processing}>
                                        {processing ? 'İŞLENİYOR...' : 'GİRİŞİ ONAYLA'}
                                    </Button>
                                </>
                            )}

                            {actionModal.type === 'exit' && (
                                <Button variant="primary" className="flex-1 !bg-red-600 hover:!bg-red-500 !text-white border-none shadow-[0_0_30px_rgba(239,68,68,0.4)]" onClick={handleExit} disabled={processing}>
                                    {processing ? 'İŞLENİYOR...' : 'ÇIKIŞI ONAYLA'}
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </Modal>

            {/* Instant Entry Modal */}
            <Modal isOpen={instantModal} onClose={() => setInstantModal(false)} title="ANLIK ARAÇ GİRİŞİ">
                <div className="p-md space-y-md">
                    <p className="text-secondary text-sm">Rezervasyonu olmayan araçlar için manuel kayıt ekranı.</p>

                    <div className="relative inline-flex items-center justify-center w-full py-4 px-8 mb-md">
                        <input
                            className="text-center font-mono font-black w-full bg-transparent border-none focus:ring-0 placeholder-slate-300"
                            placeholder="34 ABC 123"
                            value={instantForm.plateNumber}
                            onChange={(e) => setInstantForm(prev => ({ ...prev, plateNumber: e.target.value.toUpperCase() }))}
                            autoFocus
                            style={{
                                outline: 'none',
                                textTransform: 'uppercase',
                                fontSize: '60px',
                                lineHeight: '1',
                                color: 'var(--text-primary)',
                                textShadow: '0 0 8px var(--text-primary)'
                            }}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-md">
                        <div className="form-group">
                            <label className="form-label text-[10px] uppercase font-bold tracking-widest">Nakliyeci</label>
                            <Combobox
                                items={transporters.map(t => ({ label: t.companyName || t.shortName || t.name || 'Bilinmiyor', id: t.id }))}
                                value={instantForm.transporterName}
                                onChange={(item) => {
                                    setInstantForm(prev => ({ ...prev, transporterId: item?.id || null, transporterName: item?.label || '' }));
                                }}
                                onInputChange={(val) => {
                                    setInstantForm(prev => ({ ...prev, transporterId: null, transporterName: val }));
                                }}
                                placeholder="Seçin veya Yazın..."
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label text-[10px] uppercase font-bold tracking-widest">Araç Tipi</label>
                            <Combobox
                                items={vehicleTypes.map(vt => ({ label: vt.name || 'Bilinmiyor', id: vt.id }))}
                                value={instantForm.vehicleType}
                                onChange={(item) => {
                                    setInstantForm(prev => ({ ...prev, vehicleTypeId: item?.id || null, vehicleType: item?.label || '' }));
                                }}
                                onInputChange={(val) => {
                                    setInstantForm(prev => ({ ...prev, vehicleTypeId: null, vehicleType: val }));
                                }}
                                placeholder="Seçin veya Yazın..."
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-md">
                        <div className="form-group">
                            <label className="form-label text-[10px] uppercase font-bold tracking-widest">Sürücü Adı</label>
                            <input className="input" placeholder="İsim Soyisim" value={instantForm.driverName} onChange={(e) => setInstantForm(prev => ({ ...prev, driverName: e.target.value }))} />
                        </div>
                        <div className="form-group">
                            <label className="form-label text-[10px] uppercase font-bold tracking-widest">Telefon</label>
                            <input className="input font-mono" placeholder="5xx ..." value={instantForm.driverPhone} onChange={(e) => setInstantForm(prev => ({ ...prev, driverPhone: e.target.value }))} />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label text-[10px] uppercase font-bold tracking-widest">Sürücü TC / Kimlik No</label>
                        <input className="input font-mono" placeholder="TC / Pasaport No" value={instantForm.driverIdentityId} onChange={(e) => setInstantForm(prev => ({ ...prev, driverIdentityId: e.target.value }))} />
                    </div>

                    <div className="form-group">
                        <label className="form-label text-[10px] uppercase font-bold tracking-widest">Açıklama / Not</label>
                        <textarea className="textarea h-20" placeholder="Yükleme türü vb. detaylar..." value={instantForm.notes} onChange={(e) => setInstantForm(prev => ({ ...prev, notes: e.target.value }))} />
                    </div>

                    <div className="flex gap-md pt-lg">
                        <Button variant="secondary" className="flex-1" onClick={() => setInstantModal(false)}>İPTAL</Button>
                        <Button variant="primary" className="flex-2 py-md font-black text-lg shadow-[0_10px_30px_rgba(59,130,246,0.3)]" onClick={handleInstantReservation} disabled={processing || !instantForm.plateNumber}>
                            KAYIT ET VE GİRİŞ YAP
                        </Button>
                    </div>
                </div>
            </Modal>

        </div>
    );
};

export default GateEntryExit;
